import dayjs from 'dayjs'
import { ChatChannelInstance, ChatLocalMesage, MessageStatusTypes } from '../types'
import { getChannel, getUser } from '../stores'
import { Constants } from '../constants'
import { isChatGroup } from './groups'
import { isMessageDeleted, isPollMessage, isSystemMessage, isThreadMessage, messageSystemText } from './messages'
import { attachmentIcon, attachmentPreviewText } from './attachments'
import { waitFor } from '@codeleap/utils'

export type ChannelUnavailableReason = 'deleted' | 'inaccessible' | 'unavailable'

export function formatChatCreatedAt(createdAt?: string | Date) {
  if (!createdAt) return null
  const messageDate = dayjs(createdAt)
  const today = dayjs()
  const yesterday = dayjs().subtract(1, 'day')
  
  if (messageDate.isSame(today, 'day')) {
    return messageDate.format('hh:mm A')
  }
  
  if (messageDate.isSame(yesterday, 'day')) {
    return 'Yesterday'
  }
  
  return messageDate.format('DD/MM/YY')
}

export function isChatUnread(channel: ChatChannelInstance) {
  return channel?.state.unreadCount > 0
}

export function isChatMarkedAsUnread(channel: ChatChannelInstance) {
  const user = getUser()
  return channel?.data[`unread_${user?.id}`]
}

export function isChatMarkableAsRead(channel: ChatChannelInstance) {
  const isUnread = isChatUnread(channel)
  const isMarkedAsUnread = isChatMarkedAsUnread(channel)
  return isUnread || isMarkedAsUnread
}

export function isChatMuted(channel: ChatChannelInstance) {  
  if (channel?.disconnected) return false
  return channel?.muteStatus()?.muted
}

export function getChatUnreadCount(channel: ChatChannelInstance) {
  if (channel?.disconnected) return 0
  return channel?.countUnread?.() || 0
}

export function getChatUnreadMentions(channel: ChatChannelInstance) {
  if (channel?.disconnected) return 0
  return channel?.countUnreadMentions?.() || 0
}

export function chatMutedActionProps(channel: ChatChannelInstance) {
  const isMuted = isChatMuted(channel)

  return {
    text: isMuted ? 'Unmute' : 'Mute',
    icon: isMuted ? 'bell-01' : 'bell-off-01',
  }
}

export function chatPinnedActionProps(isPinned: boolean) {
  return {
    text: isPinned ? 'Unpin' : 'Pin',
    icon: isPinned ? 'pin-blocked-02' : 'pin-02',
  }
}

export function chatPreviewText({ message, channel, isMyMessage, userName }: { message: ChatLocalMesage, channel: ChatChannelInstance, isMyMessage?: boolean, userName?: string }) {
  const isDeleted = isMessageDeleted(message)

  const isPoll = isPollMessage(message)

  const isThread = isThreadMessage(message)

  if (isDeleted) {
    return Constants.deletedMessage.text
  }
  if (isPoll) {
    return message?.poll?.name || message?.pollName
  }
  const attachment = message?.attachments?.[0]
  const hasText = !!message?.text?.trim()
  const isGroup = isChatGroup(channel)
  const isMessageSystem = message?.type === 'system'

  if (isMessageSystem) {
    // @ts-ignore
    const systemType = message?.custom?.system_type
    if (systemType === 'truncate_history') {
      return null
    }

    return messageSystemText(message)
  }

  if (hasText) {
    const previewText = isGroup && !isMyMessage && !!userName ? `${userName}: ${message?.text}` : message?.text
    return isThread ? `${previewText}` : previewText
  }
  if (attachment) {
    const text = attachmentPreviewText(attachment)
    return isThread ? `${text}` : text
  }
  return null
}

type PreviewIcon = {
  name: string
  style?: any
}

type ChatPreviewMessageStatus = 'sent' | 'delivered' | 'read'

export function chatPreviewIcons({
  isMyMessage,
  message,
  previewStatus,
}: {
  message: ChatLocalMesage
  isMyMessage?: boolean
  previewStatus?: ChatPreviewMessageStatus
}): PreviewIcon[] {
  const icons: PreviewIcon[] = []
  const isDeleted = isMessageDeleted(message)
  const isPoll = isPollMessage(message)
  const isThread = isThreadMessage(message)
  const isSystem = isSystemMessage(message)
  const isFailed = message?.status === MessageStatusTypes.FAILED || message?.type === 'error'

  if (isSystem) return []

  if (isDeleted) {
    return [{ name: Constants.deletedMessage.icon }]
  }
  
  if (isPoll) {
    return [{ name: Constants.poll.icon }]
  }
  
  if (isThread) {
    icons.push({ name: 'corner-down-left' })
  }

  const attachment = message?.attachments?.[0]

  if (isMyMessage && !isFailed) {
    icons.push({
      name: previewStatus === 'sent' ? 'check' : 'checks',
      style: previewStatus === 'read' ? [Constants.messageStatus.read.color] : [Constants.messageStatus.delivered.color],
    })
  }
  if (attachment?.type && attachmentIcon(attachment)) {
    icons.push({
      name: attachmentIcon(attachment),
    })
  }
  return icons
}


export function isChatDM(channel: ChatChannelInstance) {
  return channel?.type === 'messaging'
}

export function isChatEmpty(channel: ChatChannelInstance) {
  return channel?.state?.last_message_at === null
}

export function getChatIdByCID(cid: string) {
  return cid?.split(':')?.[1]
}

export function getChatMuteOptions() {
  return [
    { label: '1 minute', value: 1 },
    { label: '8 hours', value: 8 * 60 },
    { label: '1 week', value: 7 * 24 * 60 },
    { label: 'Always', value: Infinity },
  ]
}

const REFRESH_DELAY_PER_MEMBER = 60

export async function refreshChat(memberCount: number = 1) {
  const channel = getChannel()
  const delay = Math.max(1000, Math.min(REFRESH_DELAY_PER_MEMBER * memberCount, 6000))
  await waitFor(delay)
  return channel?.watch()
}

export function getUnavailableChannelReason(error: unknown, hasActiveChannel = true): ChannelUnavailableReason | null {
  if (!hasActiveChannel) return 'unavailable'

  const streamError = error as { code?: number | null; status?: number; message?: string } | null
  const code = streamError?.code
  const status = streamError?.status
  const message = streamError?.message?.toLowerCase?.() ?? ''

  if (code === 16 || status === 404) return 'deleted'
  if (code === 17 || status === 403) return 'inaccessible'
  if (message.includes('client.disconnect() was called')) return 'unavailable'
  if (typeof status === 'number' && status >= 400 && status < 500) return 'unavailable'

  return null
}

export function getUnavailableChannelAlert(reason: ChannelUnavailableReason) {
  switch (reason) {
    case 'deleted':
      return {
        title: 'Chat Deleted',
        body: 'This chat has been deleted and you no longer have access.',
      }

    case 'inaccessible':
      return {
        title: 'Removed from Chat',
        body: 'You no longer have access to this chat.',
      }

    case 'unavailable':
      return {
        title: 'Chat unavailable',
        body: 'This chat is no longer available.',
      }
  }
}
